import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AddCetaClassroomAndFocusStudents,
  UpdateCetaClassroomAndFocusStudents
} from '../../../../../services/AnnualPlanService'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { Modal } from '../../../../customComponents/Modal'
import { AddCircle, AttachMoney, ExpandMore, Help, RemoveCircle } from '@mui/icons-material'
import { useEEMDistrictAndBuildingsOptions } from '../../../../../hooks/use-eemDistrictBuildingOptions'
import { ActivityEditSession } from '../ActivityEditSession'
import { PriorityThreeActivityName } from '../../../../../models/PriorityThreeActivityName'
import {
  useEEMOrganizationBuildingOptions
} from '../../../../../hooks/use-eemOrganizationBuildingOptions'
import { OrganizationType } from '../../../../../models/OrganizationType'
import { type SPOTCompletionStatus } from '../../../../../models/SPOTCompletionStatus'
import { useConfirmState } from '../../../../../hooks/use-budgetThresholdExceeded'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { OrganizationTypeSelector } from '../OrganizationTypeSelector'

export interface AddCetaClassroomAndFocusStudentsFormValues {
  priorityId: number
  organizationType: OrganizationType | null
  organizationId: number | null
  buildingId: number | null
  numberOfClassrooms: string
  numberOfFocusStudents: string
  budgetRequestReason: string
  budgetRequest: string
  isImplementationPhase?: boolean
  classrooms?: ClassroomFormValues[]
  needsReview?: boolean
}

export interface UpdateCetaClassroomAndFocusStudentsFormValues extends AddCetaClassroomAndFocusStudentsFormValues {
  id: number
  isUpdate?: boolean
}

export interface ClassroomFormValues {
  id?: number
  teacherName: string
  gradeLevels: string
  rcnContactName: string
  rcnContactEmail: string
  focusStudents: FocusStudentFormValues[]
  // tracking fields
  cetaCompletedInStart?: boolean | null
  cetaCompletionDate?: Date | null
  spotCompletionStatus?: SPOTCompletionStatus | null
  budgetSpent?: string | number | null
}

export interface FocusStudentFormValues {
  id?: number
  initials: string
  // tracking fields
  boyDataSubmitted?: boolean | null
  eoyDataSubmitted?: boolean | null
}

interface AdditionalDetailsFormValues {
  classrooms: ClassroomFormValues[]
}

interface Props {
  open: boolean
  initialValues?: UpdateCetaClassroomAndFocusStudentsFormValues
  priorityId: number
  planOrgId: number
  isdOrgId: number
  eemId?: number | null
  onClose: () => void
  onSaved: () => void
  implementationPhase?: boolean
  editClassroomNumber?: number
}

export const CetaClassroomsAndFocusStudentsModal = (props: Props): JSX.Element => {
  const [formValues, setFormValues] = useState<AddCetaClassroomAndFocusStudentsFormValues>({
    priorityId: props.priorityId ?? 0,
    organizationType: null,
    organizationId: null,
    buildingId: null,
    numberOfClassrooms: '',
    numberOfFocusStudents: '',
    budgetRequestReason: '',
    budgetRequest: ''
  })
  const [submitted, setIsSubmitted] = useState(false)
  const { districtAndBuildingsOptions, isLoading: isLoadingPlanOrgDistricts } = useEEMDistrictAndBuildingsOptions(props.planOrgId)
  const { buildings: isdBuildings, isLoading: isLoadingIsdBuildings } = useEEMOrganizationBuildingOptions(props.isdOrgId)
  const [modalPage, setModalPage] = useState<'edit' | 'details' | 'overBudget'>(props.editClassroomNumber != null ? 'details' : 'edit')
  const [additionalDetailsFormValues, setAdditionalDetailsFormValues] = useState({
    classrooms: props.initialValues?.classrooms ?? []
  })
  const [showCancelUpdatesModal, setShowCancelUpdatesModal] = useState(false)
  const [expandedAccordion, setExpandedAccordion] = useState<number | null>(null)
  const { confirmState, setConfirmState, overBudgetThreshold } = useConfirmState(
    props.initialValues?.budgetRequest != null ? Number(props.initialValues.budgetRequest) : undefined,
    formValues.budgetRequest !== '' ? Number(formValues.budgetRequest) : undefined
  )

  const [additionalDetailsSubmitted, setAdditionalDetailsSubmitted] = useState(false)
  const classroomFormValidator = (index: number): Record<keyof ClassroomFormValues, { required: boolean, isValid: boolean }> => {
    const classroom = additionalDetailsFormValues.classrooms[index]
    return {
      id: { required: false, isValid: true },
      teacherName: { required: true, isValid: classroom.teacherName.trim() !== '' },
      gradeLevels: { required: true, isValid: classroom.gradeLevels.trim() !== '' },
      rcnContactName: { required: true, isValid: classroom.rcnContactName.trim() !== '' },
      rcnContactEmail: { required: true, isValid: (classroom.rcnContactEmail.trim() !== '' && isValidEmail(classroom.rcnContactEmail)) },
      focusStudents: { required: true, isValid: classroom.focusStudents.every(fs => fs.initials.trim() !== '') },
      // tracking fields are not required
      cetaCompletedInStart: { required: false, isValid: true },
      cetaCompletionDate: { required: false, isValid: true },
      spotCompletionStatus: { required: false, isValid: true },
      budgetSpent: { required: false, isValid: true }
    }
  }

  const isValidEmail = (email: string): boolean => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailPattern.test(email)
  }

  const hasInvalidClassroomFormValues = (classroomIndex: number): boolean => {
    return Object.values(classroomFormValidator(classroomIndex)).some(v => v.required && !v.isValid)
  }
  const additionalDetailsFormValidators: Record<keyof AdditionalDetailsFormValues, { required: boolean, isValid: boolean }> = {
    classrooms: {
      required: true,
      isValid: additionalDetailsFormValues.classrooms.every((_, index) => Object.values(classroomFormValidator(index)).every(c => (c.required && c.isValid) || !c.required))
    }
  }
  const hasInvalidAdditionalDetailsFormValues = Object.values(additionalDetailsFormValidators).some(v => v.required && !v.isValid)

  const isEditing = props.initialValues != null

  useEffect(() => {
    if (props.initialValues != null) {
      // Only extract the fields we need to avoid circular references
      setFormValues({
        priorityId: props.initialValues.priorityId,
        organizationType: props.initialValues.organizationType,
        organizationId: props.initialValues.organizationId,
        buildingId: props.initialValues.buildingId,
        numberOfClassrooms: props.initialValues.numberOfClassrooms,
        numberOfFocusStudents: props.initialValues.numberOfFocusStudents,
        budgetRequestReason: props.initialValues.budgetRequestReason,
        budgetRequest: props.initialValues.budgetRequest,
        isImplementationPhase: props.initialValues.isImplementationPhase,
        needsReview: props.initialValues.needsReview
      })
      setAdditionalDetailsFormValues({ classrooms: props.initialValues.classrooms ?? [] })
    }
  }, [props.initialValues])

  useEffect(() => {
    if (props.editClassroomNumber != null) {
      const classroomIndex = props.initialValues?.classrooms?.findIndex(c => c.id === props.editClassroomNumber) ?? -1
      setExpandedAccordion(classroomIndex)
    }
  }, [props.editClassroomNumber])

  useEffect(() => {
    if (confirmState === 'confirmed') {
      void handleImplementationPhaseSubmit()
    }
  }, [confirmState])

  const hasInvalidFormValues = formValues.organizationType == null ||
    formValues.organizationId === null || formValues.organizationId === 0 ||
    formValues.buildingId === null || formValues.buildingId === 0 ||
    !isValidPositiveNumber(formValues.numberOfClassrooms) ||
    !isValidPositiveNumber(formValues.numberOfFocusStudents) ||
    formValues.budgetRequestReason === '' ||
    !isValidPositiveNumber(formValues.budgetRequest)

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleOrganizationTypeUpdate = (organizationType: OrganizationType, organizationId: number | null): void => {
    setFormValues({ ...formValues, organizationType, organizationId })
  }

  const handleSubmit = async (): Promise<void> => {
    if (props.implementationPhase === true) {
      await handleImplementationPhaseSubmit()
    } else {
      await handleContractPhaseSubmit()
    }
  }

  const handleContractPhaseSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please complete all fields')
      return
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateCetaClassroomAndFocusStudents({ ...formValues, id: props.initialValues.id, priorityId: props.priorityId, needsReview: overBudgetThreshold })
      : await AddCetaClassroomAndFocusStudents({ ...formValues, needsReview: overBudgetThreshold })

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success(`CETA Classrooms and Focus Students ${props.initialValues != null ? 'Updated' : 'Saved'}`)

    props.onClose()
    props.onSaved()
  }

  const handleImplementationPhaseSubmit = async (): Promise<void> => {
    if (modalPage === 'edit' || modalPage === 'overBudget') {
      setIsSubmitted(true)
      if (hasInvalidFormValues) {
        toast.error('Please fill out required fields')
        return
      }

      if (overBudgetThreshold && confirmState !== 'confirmed') {
        setConfirmState('needsConfirmation')
        setModalPage('overBudget')
        return
      }

      setModalPage('details')
      // intialize additional details form values based on number of classrooms
      const newNumberOfClassrooms = Number(formValues.numberOfClassrooms)
      const existingClassrooms = props.initialValues?.classrooms ?? []

      let classrooms: ClassroomFormValues[] = []

      const newClassroomForm = (): ClassroomFormValues => ({
        teacherName: '',
        gradeLevels: '',
        rcnContactName: '',
        rcnContactEmail: '',
        focusStudents: [{ initials: '', boyDataSubmitted: null, eoyDataSubmitted: null }],
        cetaCompletedInStart: null,
        cetaCompletionDate: null,
        spotCompletionStatus: null,
        budgetSpent: null
      })
      if (newNumberOfClassrooms > existingClassrooms.length) {
        // increase the number of classrooms
        const numberOfNewClassroomsAdded = newNumberOfClassrooms - existingClassrooms.length
        const newClassrooms: ClassroomFormValues[] = Array.from({ length: numberOfNewClassroomsAdded }, () => newClassroomForm())
        classrooms = [...existingClassrooms, ...newClassrooms]
      } else if (newNumberOfClassrooms < existingClassrooms.length) {
        // reduce the number of classrooms
        classrooms = existingClassrooms.slice(0, newNumberOfClassrooms)
      } else {
        // number of classrooms is unchanged
        classrooms = existingClassrooms
      }

      // if there were no existing classrooms, create new ones
      if (existingClassrooms.length === 0) {
        classrooms = Array.from({ length: newNumberOfClassrooms }, () => newClassroomForm())
      }
      setAdditionalDetailsFormValues({ classrooms })
    } else if (modalPage === 'details') {
      setAdditionalDetailsSubmitted(true)

      if (hasInvalidAdditionalDetailsFormValues) {
        if (additionalDetailsFormValues.classrooms?.some(c => !isValidEmail(c.rcnContactEmail)) ?? false) {
          toast.error('Please enter valid email addresses for all RCN contacts')
          return
        }
        toast.error('Please fill out required additional detail fields')
        return
      }

      const updatedClassroomsAndFocusStudents: UpdateCetaClassroomAndFocusStudentsFormValues = {
        ...formValues,
        id: props.initialValues?.id ?? 0,
        priorityId: props.priorityId,
        classrooms: additionalDetailsFormValues.classrooms,
        isUpdate: props.initialValues?.isUpdate ?? false,
        isImplementationPhase: props.implementationPhase,
        needsReview: overBudgetThreshold
      }

      const { success, errors } = await UpdateCetaClassroomAndFocusStudents(updatedClassroomsAndFocusStudents)
      if (!success) {
        errors.forEach((error) => toast.error(error))
        return
      }

      toast.success('CETA Classrooms & Focus Students Updated')

      setModalPage('edit')
      props.onClose()
      props.onSaved()
    }
  }

  const handleClose = (): void => {
    if (props.implementationPhase === true && modalPage === 'details') {
      setShowCancelUpdatesModal(true)
    } else {
      props.onClose()
    }
  }
  const handleCancel = (): void => {
    if (props.implementationPhase === true) {
      if (modalPage === 'edit') props.onClose()
      else if (modalPage === 'details') setModalPage('edit')
      return
    }

    props.onClose()
  }

  const handleConfirmCancelUpdates = async (): Promise<void> => {
    if (props.initialValues == null) return

    // Debug: Check for circular references
    try {
      console.log('formValues:', JSON.parse(JSON.stringify(formValues)))
    } catch (error) {
      console.error('Circular reference detected in formValues:', error)
      console.log('formValues keys:', Object.keys(formValues))
      Object.keys(formValues).forEach(key => {
        try {
          JSON.stringify((formValues as any)[key])
        } catch {
          console.error(`Circular reference in field: ${key}`)
        }
      })
    }

    const { success, errors } = await UpdateCetaClassroomAndFocusStudents({
      ...formValues,
      id: props.initialValues.id,
      priorityId: props.priorityId,
      isUpdate: props.initialValues.isUpdate ?? false,
      isImplementationPhase: props.implementationPhase,
      classrooms: props.initialValues.classrooms // revert to original classrooms
    })

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success('CETA Classrooms and Focus Students Updated')

    props.onClose()
    props.onSaved()
  }

  const isDistrict = formValues.organizationType === OrganizationType.District
  const selectedDistrict = districtAndBuildingsOptions.find(d => d.id.toString() === formValues.organizationId?.toString())
  const buildings = formValues.organizationType == null
    ? []
    : isDistrict
      ? (selectedDistrict?.buildings ?? [])
      : isdBuildings

  if (showCancelUpdatesModal) {
    return (
      <Modal
        open={true}
        title='Cancel Updates'
        cancelButtonText='Go Back'
        confirmButtonText='Confirm Cancel'
        data-testid='confirmCancelUpdatesModal'
        bodyContent={<Stack gap='1rem' m='24px 0'>
          <Typography variant='body1' fontWeight={600}>Are you sure you want to cancel?</Typography>
          <Typography variant='body1'>If you proceed your changes in the Additional Details section will not be saved.</Typography>
        </Stack>}
        onClose={() => { setShowCancelUpdatesModal(false) }}
        onConfirm={handleConfirmCancelUpdates}
        maxWidth='xs'
      />
    )
  }

  const handleUpdateClassroomField = (classroomIndex: number, e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    const updatedClassrooms = [...additionalDetailsFormValues.classrooms]
    updatedClassrooms[classroomIndex] = { ...updatedClassrooms[classroomIndex], [name]: value }
    setAdditionalDetailsFormValues({ ...additionalDetailsFormValues, classrooms: updatedClassrooms })
  }

  const handleUpdateFocusStudentField = (classroomIndex: number, studentIndex: number, e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    const updatedClassrooms = [...additionalDetailsFormValues.classrooms]
    const updatedFocusStudents = [...updatedClassrooms[classroomIndex].focusStudents]
    updatedFocusStudents[studentIndex] = { ...updatedFocusStudents[studentIndex], [name]: value }
    updatedClassrooms[classroomIndex] = { ...updatedClassrooms[classroomIndex], focusStudents: updatedFocusStudents }
    setAdditionalDetailsFormValues({ ...additionalDetailsFormValues, classrooms: updatedClassrooms })
  }

  const handleAddFocusStudent = (classroomIndex: number): void => {
    const updatedClassrooms = [...additionalDetailsFormValues.classrooms]
    updatedClassrooms[classroomIndex].focusStudents.push({ initials: '' })
    setAdditionalDetailsFormValues({ ...additionalDetailsFormValues, classrooms: updatedClassrooms })
  }

  const handleRemoveFocusStudent = (classroomIndex: number, studentIndex: number): void => {
    const updatedClassrooms = [...additionalDetailsFormValues.classrooms]
    const updatedFocusStudents = [...updatedClassrooms[classroomIndex].focusStudents]
    updatedFocusStudents.splice(studentIndex, 1)
    updatedClassrooms[classroomIndex] = { ...updatedClassrooms[classroomIndex], focusStudents: updatedFocusStudents }
    setAdditionalDetailsFormValues({ ...additionalDetailsFormValues, classrooms: updatedClassrooms })
  }

  return <Modal
    title='CETA Classrooms & Focus Students'
    open={props.open}
    data-testid={props?.initialValues != null ? 'editCetaClassroomsAndFocusStudentsModal' : 'addCetaClassroomsAndFocusStudentsModal'}
    width='480px'
    confirmButtonText={props.implementationPhase === true && modalPage === 'edit' ? 'Save & Continue' : 'Save'}
    cancelButtonText={props.implementationPhase === true && modalPage === 'details' ? 'Go Back' : 'Cancel'}
    onClose={handleClose}
    onCancel={handleCancel}
    onConfirm={handleSubmit}
    bodyContent={
      <Stack gap='1rem'>
        {modalPage === 'edit' && <>
          {props.implementationPhase !== true && (
            <Typography
              variant='body1'
              sx={{
                backgroundColor: 'var(--blue-50)',
                border: '1px solid var(--blue-300)',
                borderRadius: '8px',
                padding: '10px 32px'
              }}
            >
              Please fill out the following information with as much detail as you can at this time. You will have the opportunity to further define the specific classrooms & focus students during implementation.
            </Typography>
          )}
          {
            props.implementationPhase === true && props.initialValues == null &&
              <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                backgroundColor: 'var(--blue-500)',
                color: 'white',
                fill: 'white'
              }} severity="info"
              >The addition of this item will prompt a review from the START team.
              </Alert>
          }
          <Typography variant='h5'>Location</Typography>
          <OrganizationTypeSelector
            disabled={isEditing && props.implementationPhase !== true}
            required={formValues.organizationType == null}
            error={submitted && formValues.organizationType == null}
            value={formValues.organizationType}
            isdEemId={props.eemId}
            onChange={handleOrganizationTypeUpdate}
          />

          <Typography variant='h5'>{formValues.organizationType}</Typography>

          {isDistrict && (
            <FormControl
              fullWidth
              disabled={isEditing && props.implementationPhase !== true}
              required={formValues.organizationId == null}
              error={submitted && formValues.organizationId == null}
            >
              <InputLabel id="district-label">Select District</InputLabel>
              <Select
                labelId="district-label"
                id="organizationId"
                value={districtAndBuildingsOptions.some(d => d.id === formValues.organizationId) ? formValues.organizationId : ''}
                label="Select District"
                name='organizationId'
                data-testid='district'
                onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.organizationId>) => void}
                disabled={isEditing && props.implementationPhase !== true}
              >
                {isLoadingPlanOrgDistricts && <MenuItem disabled>Loading districts...</MenuItem>}
                {districtAndBuildingsOptions.length === 0 && <MenuItem disabled>No district options</MenuItem>}
                {districtAndBuildingsOptions.map((district) => (
                  <MenuItem key={district.id} value={district.id} data-testid='districtOption'>{district.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Typography variant='h5'>Building</Typography>
          <FormControl
            fullWidth
            variant='filled'
            required={formValues.buildingId == null}
            error={submitted && formValues.buildingId == null}
          >
            <InputLabel id="building-label">Select Building</InputLabel>
            <Select
              labelId="building-label"
              id="building"
              value={buildings.length > 0 && formValues.buildingId != null ? formValues.buildingId : ''}
              label="Select Building"
              name='buildingId'
              data-testid='building'
              onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.buildingId>) => void}
              disabled={isEditing && props.implementationPhase !== true}
            >
              {!isDistrict && isLoadingIsdBuildings && <MenuItem disabled>Loading buildings...</MenuItem>}
              {formValues.organizationType == null && <MenuItem disabled>Select an organization type for building options</MenuItem>}
              {isDistrict && formValues.organizationId == null && <MenuItem disabled>Select a district for building options</MenuItem>}
              {(formValues.organizationId != null && formValues.organizationType != null) && buildings.length === 0 && <Typography variant='body1' textAlign={'center'}>No building options</Typography>}
              {buildings.length > 0 && buildings.map((building) => (
                <MenuItem key={building.id} value={building.id} data-testid='buildingOption'>{building.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '300px' }}>
            <TextField
              sx={{ width: '250px' }}
              variant='filled'
              name='numberOfClassrooms'
              label='Number of Classrooms'
              value={formValues.numberOfClassrooms}
              data-testid='numberOfClassrooms'
              onChange={handleFieldUpdate}
              required={formValues.numberOfClassrooms === ''}
              error={submitted && !isValidPositiveNumber(formValues.numberOfClassrooms)}
            />
            <Tooltip componentsProps={{ tooltip: { sx: { backgroundColor: 'var(--grey-900)' } } }} title={<Box>
              <Typography sx={{ color: 'white' }} variant='subtitle1'>Number of Classrooms</Typography>
              <Typography sx={{ color: 'white' }} variant='body1'>Provide your best estimate. You will be able to update this at implementation.</Typography>
            </Box>}
            >
              <Help sx={{ color: 'var(--grey-500)', width: '.9em', height: '.9em' }} />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '300px' }}>
            <TextField
              sx={{ width: '250px' }}
              name='numberOfFocusStudents'
              label='Number of Focus Students'
              variant='filled'
              value={formValues.numberOfFocusStudents}
              data-testid='numberOfFocusStudents'
              onChange={handleFieldUpdate}
              required={formValues.numberOfFocusStudents === ''}
              error={submitted && !isValidPositiveNumber(formValues.numberOfFocusStudents)}
            />
            <Tooltip componentsProps={{ tooltip: { sx: { backgroundColor: 'var(--grey-900)' } } }} title={<Box>
              <Typography sx={{ color: 'white' }} variant='subtitle1'>Number of Focus Students</Typography>
              <Typography sx={{ color: 'white' }} variant='body1'>Provide your best estimate. You will be able to update this at implementation.</Typography>
            </Box>}
            >
              <Help sx={{ color: 'var(--grey-500)', width: '.9em', height: '.9em' }} />
            </Tooltip>
          </Box>

          <Typography variant='h5'>Budget Request</Typography>
          <TextField
            name='budgetRequestReason'
            label='Resources'
            value={formValues.budgetRequestReason}
            data-testid='budgetRequestReason'
            onChange={handleFieldUpdate}
            variant='filled'
            multiline={true}
            rows={4}
            required={formValues.budgetRequestReason === ''}
            error={submitted && formValues.budgetRequestReason === ''}
          />

          <TextField
            variant='filled'
            sx={{ width: 'fit-content' }}
            onChange={handleFieldUpdate}
            label={'Budget Request'}
            data-testid='budgetRequest'
            InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }}
            name={'budgetRequest'}
            required={formValues.budgetRequest === ''}
            error={submitted && !isValidPositiveNumber(formValues.budgetRequest)}
            value={formValues.budgetRequest}
          />

          {props.initialValues != null &&
            <ActivityEditSession
              activityId={props.initialValues.id}
              activityName={PriorityThreeActivityName.CetaClassroomsAndFocusStudents}
              onClose={props.onClose}
            />
            }
        </>}

        {modalPage === 'overBudget' && <>
          <BudgetThresholdExceededModal
            onClose={props.onClose}
            onConfirm={() => {
              setConfirmState('confirmed')
            }}
          />
        </>}

        {modalPage === 'details' && <>
          <Typography variant='h4'>Additional Details</Typography>
          <Typography variant='h5'>Classroom details</Typography>
          <Typography variant='body1'>Please complete the additional information for each focus classroom before continuing.</Typography>
          {additionalDetailsFormValues.classrooms.map((classroom, classroomIndex) => {
            const isValid = !hasInvalidClassroomFormValues(classroomIndex)
            return (
              <Stack
                key={classroomIndex}
                gap='1rem'
                sx={{
                  border: '1px solid var(--figma-tablegrey)',
                  borderRadius: '8px'
                }}
              >
                <Accordion
                  expanded={expandedAccordion === classroomIndex}
                  onChange={(_, isExpanded) => { setExpandedAccordion(isExpanded ? classroomIndex : null) }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls={`classroomAccordionContent-${classroomIndex}`}
                    id={`classroomAccordionHeader-${classroomIndex}`}
                    data-testid={`classroomAccordionHeader-${classroomIndex}`}
                  >
                    <Typography
                      component="span"
                      variant='h5'
                      fontSize='18px'
                      color={theme => additionalDetailsSubmitted && !isValid ? theme.palette.error.main : 'var(--black)'}
                    >
                      CETA Classroom {classroomIndex + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <TextField
                      id={`classroomTeacherName-${classroomIndex}`}
                      name='teacherName'
                      label='Teacher Name'
                      value={classroom.teacherName}
                      data-testid={`classroomTeacherName-${classroomIndex}`}
                      onChange={(e) => { handleUpdateClassroomField(classroomIndex, e) }}
                      variant='filled'
                      required={classroomFormValidator(classroomIndex).teacherName.required}
                      error={additionalDetailsSubmitted && !classroomFormValidator(classroomIndex).teacherName.isValid}
                    />

                    <TextField
                      variant='filled'
                      name='gradeLevels'
                      label='Grade Level(s)'
                      sx={{ width: 'fit-content' }}
                      value={classroom.gradeLevels}
                      id={`classroomGradeLevels-${classroomIndex}`}
                      data-testid={`classroomGradeLevels-${classroomIndex}`}
                      onChange={(e) => { handleUpdateClassroomField(classroomIndex, e) }}
                      helperText='Separate with commas; 4th, 5th, etc.'
                      required={classroomFormValidator(classroomIndex).gradeLevels.required}
                      error={additionalDetailsSubmitted && !classroomFormValidator(classroomIndex).gradeLevels.isValid}
                    />

                    <TextField
                      id={`classroomRcnContactName-${classroomIndex}`}
                      name='rcnContactName'
                      label='RCN Contact Name'
                      value={classroom.rcnContactName}
                      data-testid={`classroomRcnContactName-${classroomIndex}`}
                      onChange={(e) => { handleUpdateClassroomField(classroomIndex, e) }}
                      variant='filled'
                      required={classroomFormValidator(classroomIndex).rcnContactName.required}
                      error={additionalDetailsSubmitted && !classroomFormValidator(classroomIndex).rcnContactName.isValid}
                    />

                    <TextField
                      id={`classroomRcnContactEmail-${classroomIndex}`}
                      name='rcnContactEmail'
                      label='RCN Contact Email'
                      value={classroom.rcnContactEmail}
                      data-testid={`classroomRcnContactEmail-${classroomIndex}`}
                      onChange={(e) => { handleUpdateClassroomField(classroomIndex, e) }}
                      variant='filled'
                      required={classroomFormValidator(classroomIndex).rcnContactEmail.required}
                      error={additionalDetailsSubmitted && !classroomFormValidator(classroomIndex).rcnContactEmail.isValid}
                    />

                    <Typography variant='body1' fontWeight={600}>Focus Students</Typography>
                    {classroom.focusStudents.map((student, studentIndex) => (
                      <Box
                        key={studentIndex}
                        sx={{
                          border: '1px solid var(--grey-200)',
                          borderRadius: '8px',
                          padding: '8px',
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '.5rem'
                        }}
                      >
                        <TextField
                          id={`classroom-${classroomIndex}-focusStudentInitials-${studentIndex}`}
                          name='initials'
                          label={'Student Initials'}
                          value={student.initials}
                          data-testid={`classroom-${classroomIndex}-focusStudentInitials-${studentIndex}`}
                          onChange={(e) => { handleUpdateFocusStudentField(classroomIndex, studentIndex, e) }}
                          variant='filled'
                          required={classroomFormValidator(classroomIndex).focusStudents.required}
                          error={additionalDetailsSubmitted && !classroomFormValidator(classroomIndex).focusStudents.isValid}
                        />

                        {classroom.focusStudents.length > 1 && (
                          <Button
                            size='small'
                            sx={{ width: 'fit-content', color: 'var(--black)' }}
                            data-testid={`classroom-${classroomIndex}-removeFocusStudentButton-${studentIndex}`}
                            onClick={handleRemoveFocusStudent.bind(null, classroomIndex, studentIndex)}
                          >
                            Remove Student
                            <RemoveCircle sx={{ ml: '.5rem', color: 'var(--red-700)', fontSize: 'inherit' }} />
                          </Button>
                        )}
                      </Box>
                    ))}
                    <Button
                      sx={{ width: 'fit-content', color: 'var(--black)' }}
                      data-testid={`classroom-${classroomIndex}-addFocusStudentButton`}
                      onClick={handleAddFocusStudent.bind(null, classroomIndex)}
                    >
                      Add Student
                      <AddCircle sx={{ ml: '.5rem', color: 'var(--blue-700)', fontSize: 'inherit' }} />
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            )
          })}
        </>}
      </Stack>
    }
  />
}
